import './EmbeddedApplication.css';

import { IonText } from '@ionic/react';
import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useUserState } from '../../hooks/context/useUserState';
import { buildEmbeddedApplicationUrl } from '../../utils/embeddedApps/buildEmbeddedApplicationUrl';
import { findMenuApplication } from '../../utils/menu/findMenuApplication';

const EmbeddedApplication = () => {
  const { appId } = useParams<{ appId: string }>();
  const history = useHistory();
  const { t, language } = useTranslate();
  const {
    state: { menuItems, token },
  } = useUserState();
  const frameRef = useRef<HTMLIFrameElement>(null);

  const menuItem = useMemo(
    () => findMenuApplication(menuItems, appId),
    [appId, menuItems]
  );
  const source = useMemo(
    () =>
      menuItem?.app
        ? buildEmbeddedApplicationUrl({
            ...menuItem.app,
            locale: language,
          })
        : null,
    [language, menuItem]
  );
  const allowedOrigin = useMemo(() => {
    if (!menuItem?.app) return null;
    try {
      const origin = new URL(menuItem.app.link, window.location.href).origin;
      return origin === 'null' ? null : origin;
    } catch {
      return null;
    }
  }, [menuItem]);

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const frameWindow = frameRef.current?.contentWindow;
      if (
        !allowedOrigin ||
        event.origin !== allowedOrigin ||
        !frameWindow ||
        event.source !== frameWindow
      ) {
        return;
      }

      const data = event.data as { type?: string } | null;
      if (data?.type === 'app:close') {
        history.goBack();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [allowedOrigin, history]);

  if (!source || !allowedOrigin) {
    return (
      <IonText color="danger" className="embedded-application-page__error">
        {t('app.unavailable')}
      </IonText>
    );
  }

  return (
    <div className="embedded-application-page">
      <iframe
        ref={frameRef}
        className="embedded-application-page__frame"
        src={source}
        title={menuItem?.label ?? t('app.title')}
        onLoad={() => {
          if (token && allowedOrigin && frameRef.current?.contentWindow) {
            frameRef.current.contentWindow.postMessage(
              { type: 'app:token', token },
              allowedOrigin
            );
          }
        }}
        referrerPolicy="origin"
      />
    </div>
  );
};

export default EmbeddedApplication;
